import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './AddRechnungWizard.css';
import { RoundTable, BillingPosition, RoundTableBilling, ExcelData, ProjectFile } from './wizardTypes';
import WizardStepIndicator from './WizardStepIndicator';
import WizardStep1 from './WizardStep1';
import WizardStep2 from './WizardStep2';
import WizardStep3 from './WizardStep3';
import WizardStep4 from './WizardStep4';

interface AddRechnungWizardProps {
    projectId: number;
    projectName: string;
    onClose: () => void;
    onSuccess: () => void;
    token: string;
}

const AddRechnungWizard: React.FC<AddRechnungWizardProps> = ({
    projectId,
    projectName,
    onClose,
    onSuccess,
    token,
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [error, setError] = useState<string | null>(null);

    // Step 1: General Information
    const [billType, setBillType] = useState<'single' | 'all'>('all');
    const [selectedRoundTableId, setSelectedRoundTableId] = useState<number | null>(null);
    const [roundTables, setRoundTables] = useState<RoundTable[]>([]);
    const [loadingRoundTables, setLoadingRoundTables] = useState(true);

    // Step 2: Billing Positions
    const [positions, setPositions] = useState<BillingPosition[]>([]);
    const [newPosition, setNewPosition] = useState<BillingPosition>({
        id: '',
        description: '',
        priceType: 'fixed',
        amount: 0,
        taxRate: 19
    });

    // Step 3: Member List Upload
    const [memberListFile, setMemberListFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [excelData, setExcelData] = useState<ExcelData | null>(null);
    const [selectedTableNumberField, setSelectedTableNumberField] = useState<string>('');
    const [selectedStatusFields, setSelectedStatusFields] = useState<string[]>([]);
    const [memberCounts, setMemberCounts] = useState<Record<number, number>>({});
    const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([]);
    const [fileSource, setFileSource] = useState<'upload' | 'existing'>('upload');
    const [selectedProjectFileId, setSelectedProjectFileId] = useState<number | null>(null);
    const [uploadedProjectFileId, setUploadedProjectFileId] = useState<number | null>(null);
    const [loadingExistingFile, setLoadingExistingFile] = useState(false);

    // Step 4: Review
    const [roundTableBillings, setRoundTableBillings] = useState<RoundTableBilling[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isCreatingInvoices, setIsCreatingInvoices] = useState(false);
    const [creationProgress, setCreationProgress] = useState({ current: 0, total: 0 });

    // Fetch round tables
    useEffect(() => {
        const fetchRoundTables = async () => {
            try {
                const response = await fetch('/api/roundtables', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch round tables');
                }

                const data: RoundTable[] = await response.json();
                setRoundTables(data);
                if (data.length > 0) {
                    setSelectedRoundTableId(data[0].id);
                }
            } catch (error) {
                console.error('Error fetching round tables:', error);
                toast.error('Fehler beim Laden der Round Tables');
            } finally {
                setLoadingRoundTables(false);
            }
        };

        fetchRoundTables();
    }, [token]);

    // Fetch project files
    useEffect(() => {
        const fetchProjectFiles = async () => {
            try {
                const response = await fetch(`/api/projects/${projectId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch project files');
                }

                const data = await response.json();
                setProjectFiles(data.projectFiles || []);
            } catch (error) {
                console.error('Error fetching project files:', error);
            }
        };

        fetchProjectFiles();
    }, [projectId, token]);

    // Calculate billings when entering step 4 or when member counts change
    useEffect(() => {
        if (currentStep === 4) {
            calculateBillings();
        }
    }, [currentStep, memberCounts, positions, roundTables, billType, selectedRoundTableId]);

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const addPosition = () => {
        if (!newPosition.description.trim()) {
            setError('Bitte geben Sie eine Beschreibung ein');
            return;
        }
        if (newPosition.amount <= 0) {
            setError('Bitte geben Sie einen gültigen Betrag ein');
            return;
        }

        const position = {
            ...newPosition,
            id: Date.now().toString()
        };
        setPositions([...positions, position]);
        setNewPosition({
            id: '',
            description: '',
            priceType: 'fixed',
            amount: 0,
            taxRate: 19
        });
        setError(null);
    };

    const removePosition = (id: string) => {
        setPositions(positions.filter(p => p.id !== id));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setMemberListFile(e.target.files[0]);
            // Reset previous upload data
            setExcelData(null);
            setSelectedTableNumberField('');
            setSelectedStatusFields([]);
            setUploadedProjectFileId(null);
        }
    };

    const uploadExcel = async () => {
        if (!memberListFile) {
            toast.error('Bitte wählen Sie eine Datei aus');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', memberListFile);

            const response = await fetch(`/api/projects/${projectId}/upload-excel`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Failed to upload Excel file');
            }

            const result = await response.json();
            setExcelData({
                columns: result.columns,
                data: result.data,
                rowCount: result.rowCount,
            });
            setUploadedProjectFileId(result.projectFileId);
            toast.success('Datei erfolgreich hochgeladen');
        } catch (error) {
            console.error('Error uploading Excel file:', error);
            toast.error('Fehler beim Hochladen der Datei');
        } finally {
            setUploading(false);
        }
    };

    const loadExistingFile = async () => {
        if (!selectedProjectFileId) {
            toast.error('Bitte wählen Sie eine Datei aus');
            return;
        }

        setLoadingExistingFile(true);
        try {
            // Call endpoint to parse the existing file without re-uploading
            const response = await fetch(`/api/projects/files/${selectedProjectFileId}/parse`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to parse Excel file');
            }

            const result = await response.json();
            setExcelData({
                columns: result.columns,
                data: result.data,
                rowCount: result.rowCount,
            });
            toast.success('Datei erfolgreich geladen');
        } catch (error) {
            console.error('Error loading existing file:', error);
            toast.error('Fehler beim Laden der Datei');
        } finally {
            setLoadingExistingFile(false);
        }
    };

    const processExcelData = () => {
        if (!excelData || !selectedTableNumberField) {
            return;
        }

        // Find the status column (case-insensitive)
        const statusColumn = excelData.columns.find(col =>
            col.toLowerCase().includes('status')
        );

        // Count members by table number, filtering by status values if selected
        const counts: Record<number, number> = {};

        excelData.data.forEach((row) => {
            // Check if this row should be counted based on selected status values
            let shouldCount = true;
            if (selectedStatusFields.length > 0 && statusColumn) {
                // Count only if the status column value matches one of the selected values
                const statusValue = String(row[statusColumn] || '');
                shouldCount = selectedStatusFields.includes(statusValue);
            }

            if (shouldCount) {
                const tableNumber = row[selectedTableNumberField];
                if (tableNumber !== undefined && tableNumber !== null && tableNumber !== '') {
                    const tableNum = Number(tableNumber);
                    if (!isNaN(tableNum)) {
                        counts[tableNum] = (counts[tableNum] || 0) + 1;
                    }
                }
            }
        });

        setMemberCounts(counts);
    };

    const calculateBillings = () => {
        const hasPerTablerPosition = positions.some(p => p.priceType === 'perTabler');

        let tablesToBill: RoundTable[] = [];
        if (billType === 'single' && selectedRoundTableId) {
            const table = roundTables.find(rt => rt.id === selectedRoundTableId);
            if (table) tablesToBill = [table];
        } else {
            tablesToBill = roundTables;
        }

        const billings: RoundTableBilling[] = tablesToBill.map(rt => {
            // Use member count from Excel data if available, otherwise use 0
            let tablerCount = 0;
            if (hasPerTablerPosition) {
                if (memberCounts[rt.number] !== undefined) {
                    tablerCount = memberCounts[rt.number];
                } else {
                    tablerCount = 0;
                }
            }

            const totalAmount = positions.reduce((sum, pos) => {
                if (pos.priceType === 'fixed') {
                    return sum + pos.amount;
                } else {
                    return sum + (pos.amount * tablerCount);
                }
            }, 0);

            return {
                roundTable: rt,
                positions: [...positions],
                tablerCount,
                totalAmount,
                selected: true
            };
        });

        setRoundTableBillings(billings);
    };

    const handleNext = () => {
        setError(null);

        if (currentStep === 1) {
            if (billType === 'single' && !selectedRoundTableId) {
                setError('Bitte wählen Sie einen Round Table aus');
                return;
            }
            setCurrentStep(2);
        } else if (currentStep === 2) {
            if (positions.length === 0) {
                setError('Bitte fügen Sie mindestens eine Position hinzu');
                return;
            }
            const hasPerTablerPosition = positions.some(p => p.priceType === 'perTabler');
            if (hasPerTablerPosition) {
                setCurrentStep(3);
            } else {
                calculateBillings();
                setCurrentStep(4);
            }
        } else if (currentStep === 3) {
            // Validate and process Excel data if uploaded
            if (excelData) {
                if (!selectedTableNumberField) {
                    setError('Bitte wählen Sie ein Feld für die Tischnummer aus');
                    return;
                }
                // Process Excel data to update member counts
                processExcelData();
            }
            setCurrentStep(4);
        }
    };

    const handleBack = () => {
        setError(null);
        if (currentStep === 2) {
            setCurrentStep(1);
        } else if (currentStep === 3) {
            setCurrentStep(2);
        } else if (currentStep === 4) {
            const hasPerTablerPosition = positions.some(p => p.priceType === 'perTabler');
            setCurrentStep(hasPerTablerPosition ? 3 : 2);
        }
    };

    const handleConfirm = async () => {
        const selectedBillings = roundTableBillings.filter(b => b.selected);
        if (selectedBillings.length === 0) {
            setError('Bitte wählen Sie mindestens einen Round Table aus');
            return;
        }

        setIsCreatingInvoices(true);
        setCreationProgress({ current: 0, total: selectedBillings.length });

        let successCount = 0;
        let failCount = 0;

        // Create invoices consecutively
        for (let i = 0; i < selectedBillings.length; i++) {
            const billing = selectedBillings[i];

            try {
                // Determine the project file ID to use for the rechnung
                // Use the selected project file ID or the uploaded file ID
                const projectFileId = fileSource === 'existing' ? selectedProjectFileId : uploadedProjectFileId;

                // Create filter settings if we have a project file and it's used for per-tabler positions
                let projectFileFilter = null;
                const hasPerTablerPosition = billing.positions.some(pos => pos.priceType === 'perTabler');
                if (projectFileId && hasPerTablerPosition && selectedTableNumberField) {
                    projectFileFilter = JSON.stringify({
                        clubNumberColumn: selectedTableNumberField,
                        statusColumns: selectedStatusFields
                    });
                }

                // Transform positions to match API expected format
                const apiPositions = billing.positions.map(pos => {
                    let pricePerItem: number;
                    let numberOfItems: number;

                    if (pos.priceType === 'fixed') {
                        // For fixed price: pricePerItem = amount, numberOfItems = 1
                        pricePerItem = Math.round(pos.amount * 100); // Convert to cents
                        numberOfItems = 1;
                    } else {
                        // For per-tabler price: pricePerItem = amount, numberOfItems = tablerCount
                        pricePerItem = Math.round(pos.amount * 100); // Convert to cents
                        numberOfItems = billing.tablerCount;
                    }

                    return {
                        description: pos.description,
                        pricePerItem: pricePerItem,
                        numberOfItems: numberOfItems,
                        tax: pos.taxRate
                    };
                });

                // Create the Rechnung
                const response = await fetch('/api/rechnungen', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        date: new Date().toISOString(),
                        projectId: projectId,
                        roundTableId: billing.roundTable.id,
                        positions: apiPositions,
                        projectFileUsedId: projectFileId,
                        projectFileFilter: projectFileFilter
                    })
                });

                if (!response.ok) {
                    throw new Error(`Failed to create invoice for ${billing.roundTable.name}`);
                }

                successCount++;
            } catch (error) {
                console.error(`Error creating invoice for ${billing.roundTable.name}:`, error);
                failCount++;
            }

            // Update progress
            setCreationProgress({ current: i + 1, total: selectedBillings.length });
        }

        setIsCreatingInvoices(false);

        // Show single summary toast
        if (failCount === 0) {
            toast.success(`${successCount} Rechnung(en) erfolgreich erstellt`);
        } else if (successCount === 0) {
            toast.error(`Alle ${failCount} Rechnung(en) konnten nicht erstellt werden`);
        } else {
            toast.warning(`${successCount} Rechnung(en) erfolgreich erstellt, ${failCount} fehlgeschlagen`);
        }

        // Close wizard and refresh if at least one was successful
        if (successCount > 0) {
            onSuccess();
            onClose();
        }
    };

    const toggleBillingSelection = (roundTableId: number) => {
        setRoundTableBillings(roundTableBillings.map(b =>
            b.roundTable.id === roundTableId ? { ...b, selected: !b.selected } : b
        ));
    };

    return (
        <div className="wizard-overlay" onClick={handleOverlayClick}>
            <div className="wizard-modal wizard-modal-large">
                <div className="wizard-header">
                    <h2>Neue Rechnung hinzufügen</h2>
                    <button
                        className="wizard-close-btn"
                        onClick={onClose}
                        aria-label="Schließen"
                    >
                        ✕
                    </button>
                </div>

                <div className="wizard-content">
                    <div className="wizard-info">
                        <p className="project-info">
                            <strong>Projekt:</strong> {projectName}
                        </p>
                    </div>

                    <WizardStepIndicator
                        currentStep={currentStep}
                        hasPerTablerPosition={positions.some(p => p.priceType === 'perTabler')}
                    />

                    <div className="wizard-steps">
                        {currentStep === 1 && (
                            <WizardStep1
                                billType={billType}
                                setBillType={setBillType}
                                selectedRoundTableId={selectedRoundTableId}
                                setSelectedRoundTableId={setSelectedRoundTableId}
                                roundTables={roundTables}
                                loadingRoundTables={loadingRoundTables}
                            />
                        )}
                        {currentStep === 2 && (
                            <WizardStep2
                                positions={positions}
                                newPosition={newPosition}
                                setNewPosition={setNewPosition}
                                addPosition={addPosition}
                                removePosition={removePosition}
                            />
                        )}
                        {currentStep === 3 && (
                            <WizardStep3
                                memberListFile={memberListFile}
                                handleFileChange={handleFileChange}
                                uploadExcel={uploadExcel}
                                uploading={uploading}
                                excelData={excelData}
                                selectedTableNumberField={selectedTableNumberField}
                                setSelectedTableNumberField={setSelectedTableNumberField}
                                selectedStatusFields={selectedStatusFields}
                                setSelectedStatusFields={setSelectedStatusFields}
                                projectFiles={projectFiles}
                                fileSource={fileSource}
                                setFileSource={setFileSource}
                                selectedProjectFileId={selectedProjectFileId}
                                setSelectedProjectFileId={setSelectedProjectFileId}
                                loadExistingFile={loadExistingFile}
                                loadingExistingFile={loadingExistingFile}
                            />
                        )}
                        {currentStep === 4 && (
                            <WizardStep4
                                roundTableBillings={roundTableBillings}
                                setRoundTableBillings={setRoundTableBillings}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                positions={positions}
                                toggleBillingSelection={toggleBillingSelection}
                            />
                        )}
                    </div>

                    {error && (
                        <div className="wizard-error">
                            {error}
                        </div>
                    )}

                    {isCreatingInvoices && (
                        <div className="wizard-progress">
                            <p>Erstelle Rechnungen... ({creationProgress.current} von {creationProgress.total})</p>
                            <div className="progress-bar">
                                <div
                                    className="progress-bar-fill"
                                    style={{ width: `${(creationProgress.current / creationProgress.total) * 100}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    <div className="wizard-actions">
                        <button
                            type="button"
                            className="wizard-btn wizard-btn-cancel"
                            onClick={onClose}
                            disabled={isCreatingInvoices}
                        >
                            Abbrechen
                        </button>
                        {currentStep > 1 && (
                            <button
                                type="button"
                                className="wizard-btn wizard-btn-back"
                                onClick={handleBack}
                                disabled={isCreatingInvoices}
                            >
                                Zurück
                            </button>
                        )}
                        {currentStep < 4 ? (
                            <button
                                type="button"
                                className="wizard-btn wizard-btn-submit"
                                onClick={handleNext}
                                disabled={isCreatingInvoices}
                            >
                                Weiter
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="wizard-btn wizard-btn-submit"
                                onClick={handleConfirm}
                                disabled={isCreatingInvoices}
                            >
                                {isCreatingInvoices ? 'Erstelle...' : 'Bestätigen'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddRechnungWizard;
